import { Capacitor } from '@capacitor/core';
import { Media } from '@capacitor-community/media';
import { FileOpener } from '@capacitor-community/file-opener';
import { Filesystem, Directory } from '@capacitor/filesystem';

export const convertBlobToBase64 = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.readAsDataURL(blob);
  });

export const ensureNativeAndroidAlbum = async (albumName: string): Promise<string> => {
  const albumIdentifier = await getAlbumIdentifier(albumName);

  if (albumIdentifier) {
    return albumIdentifier;
  }

  await Media.createAlbum({ name: albumName });
  return getAlbumIdentifier(albumName);
};

export const getAlbumIdentifier = async (albumName: string): Promise<string> => {
  const { albums } = await Media.getAlbums();
  const album = albums.find((a) => a.name === albumName);
  return album?.identifier || '';
};

export const downloadImageOrVideo = async (imageOrVideoUrl: string, fileType: 'image' | 'video') => {
  let filename = imageOrVideoUrl.split('/').pop() || 'download.png';
  // remove all URL query params that might be in source URL
  filename = filename.split('?')[0];
  const cleanUrl = imageOrVideoUrl.split('?')[0];

  if (!Capacitor.isNativePlatform()) {
    const response = await fetch(cleanUrl);
    const blob = await response.blob();
    downloadFileOnWeb(blob, filename);
    return;
  }

  // Download directly to disk (Memory-Safe)
  // We use Directory.Cache as a temporary staging area
  const downloadResult = await Filesystem.downloadFile({
    url: cleanUrl,
    path: filename,
    directory: Directory.Cache,
  });

  const fileUri = await Filesystem.getUri({
    directory: Directory.Cache,
    path: filename,
  });

  const albumIdentifier = Capacitor.getPlatform() === 'android' ? await ensureNativeAndroidAlbum('iSophi') : undefined;

  if (fileType === 'image') {
    await Media.savePhoto({
      path: fileUri.uri,
      albumIdentifier: albumIdentifier,
      fileName: filename,
    });
  } else {
    await Media.saveVideo({
      path: fileUri.uri,
      albumIdentifier: albumIdentifier,
      fileName: filename,
    });
  }

  // 5. Cleanup temp file
  await Filesystem.deleteFile({
    path: filename,
    directory: Directory.Cache,
  });
};

/**
 * Use to download files NOT IMAGES!!
 */
export const downloadFile = async (fileUrl: string) => {
  const filename = fileUrl.split('/').pop() || 'download.pdf';

  const response = await fetch(fileUrl);
  const blob = await response.blob();

  const dir = Capacitor.getPlatform() === 'ios' ? Directory.Data : Directory.Documents;

  // download for native iOS/Android devices
  if (Capacitor.isNativePlatform()) {
    const base64 = await convertBlobToBase64(blob);
    await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: dir,
      recursive: true,
    });

    const fileUri = await Filesystem.getUri({
      directory: dir,
      path: filename,
    });

    let mimeType = 'application/pdf';

    // ignore application/octet-stream because iOS refuse to open such a file
    if (blob.type && blob.type !== 'application/octet-stream') {
      mimeType = blob.type;
    }

    await FileOpener.open({
      filePath: fileUri.uri,
      contentType: mimeType,
      openWithDefault: true,
    });
    return;
  }

  // download for web
  downloadFileOnWeb(blob, filename);
};

export const downloadFileOnWeb = (file: Blob, filename: string) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(file);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getFileSizeString = (fileSize: number) => {
  if (fileSize === 0) return '0B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.floor(Math.log(fileSize) / Math.log(1024));
  const formattedSize = (fileSize / Math.pow(1024, index)).toFixed(2);
  return `${formattedSize} ${units[index]}`;
};
